import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit, SimpleChanges } from '@angular/core';
import { OneWebComponentsAngularModule } from '@one/angular';
import { Router } from '@angular/router';
import { AppUtils } from '../utils/app-utils';
import { CustomerNotificationService } from '../main-page/customer-notification.service';
import { NgClass } from '@angular/common';
import { snackbarService } from '@one/web-components';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'dl-notification-table',
  standalone: true,
  imports: [OneWebComponentsAngularModule, NgClass, TranslocoModule],
  templateUrl: './notification-table.component.html',
  styleUrl: './notification-table.component.scss',
  // NOTE: deliberately NOT providing CustomerNotificationService here so the
  // singleton (providedIn: 'root') is shared with the details page; otherwise
  // toggle state changes would not propagate between the two views.
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class NotificationTableComponent  implements OnInit{
  readonly translocoService = inject(TranslocoService);
  appUtils = inject(AppUtils); 
  router = inject(Router); 
  changeDetect = inject(ChangeDetectorRef); 
  customerservice = inject(CustomerNotificationService); 
  @Input() tableData: any[] = [];
  @Input() accSelected:string;
  @Input() filterOptSel: { types: string[]; statuses: string[] } 
  @Input() searchValueSel:string;
  initPageLoad = false;
  filterBool = false;
  searchBool = false;
  previousAccSel: any;
  previousOptSel: any;
  previousSearchSel: any;
  previoustableData: any;
  ogTableData: any;
  spinBool = false;
  sortInfo: { column: string, direction: string }[] = [{ column: 'DateDelivered', direction: 'desc' }];

  ngOnChanges(changes: SimpleChanges){
    if (changes['accSelected'] && changes['accSelected'].currentValue !== this.previousAccSel) {
      this.previousAccSel = changes['accSelected'].currentValue;
      this.filterOptSel = { types: [], statuses: [] };
      this.spinBool = true;
    }
    if (changes['filterOptSel'] && changes['filterOptSel'].currentValue !== this.previousOptSel) {
      this.previousOptSel = changes['filterOptSel'].currentValue;
      this.filterOptSelected(this.filterOptSel);
    }
    if (changes['searchValueSel'] && changes['searchValueSel'].currentValue !== this.previousSearchSel) {
      this.previousSearchSel = changes['searchValueSel'].currentValue;
      this.searchInput(this.searchValueSel || '');
    }
    if (changes['tableData'] && changes['tableData'].currentValue !== this.previoustableData) {
      this.tableData = this.applySorting(this.tableData);
      this.previoustableData = changes['tableData'].currentValue;
      this.ogTableData = [...this.tableData];
      this.spinBool = false;
      if(this.tableData.length < 1) {
        this.initPageLoad = true;
      }else{
        this.initPageLoad = false;
      }
    }
  }
  
  ngOnInit(): void {
    // Re-render whenever the 'Not Read' set changes from anywhere
    // (this view, the details page, another tab restoring localStorage, etc.).
    this.customerservice.notReadIds$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.notReadFilterActive) {
          // When filtering by 'Not Read', drop rows that are no longer toggled on
          // and re-include any that were just toggled on among the cached source data.
          const visible = (this.ogTableData || []).filter(item => this.customerservice.isNotRead(item));
          this.tableData = this.applySorting(visible);
        }
        this.changeDetect.detectChanges();
      });
  }

  // Tracks whether the user is currently filtering by the frontend-only 'Not Read' status
  private notReadFilterActive = false;
  // Backend statuses that may visually appear as 'Not Read' (only 'Read' today)
  private readonly notReadSourceStatuses = ['Read'];

  filterOptSelected(filterValue){
    if(filterValue?.typeFilters.length <= 0 ){
      filterValue.typeFilters = ['all'];
    }
    if(filterValue?.statusFilters.length <= 0 ){
      filterValue.statusFilters = ['all'];
    }

    // Strip the frontend-only 'Not Read' status before contacting the backend
    // and remember that the user wants to see only the locally-toggled items.
    const notReadStatus = 'Not Read';
    const selectedStatuses: string[] = filterValue.statusFilters || [];
    this.notReadFilterActive = selectedStatuses.includes(notReadStatus);
    let backendStatuses = selectedStatuses.filter(s => s !== notReadStatus);
    if (this.notReadFilterActive) {
      // Make sure the backend returns the rows that 'Not Read' could have been derived from
      const merged = new Set<string>(backendStatuses.filter(s => s !== 'all'));
      this.notReadSourceStatuses.forEach(s => merged.add(s));
      backendStatuses = Array.from(merged);
    }
    if (backendStatuses.length === 0) {
      backendStatuses = ['all'];
    }

    if(this.accSelected === 'My notifications'){
      this.getTableFilterData(filterValue.typeFilters, backendStatuses);
    }
    if(this.accSelected !== 'My notifications'){
      this.getAccFilterData(this.accSelected, filterValue.typeFilters, backendStatuses)
    }
  }

  /**
   * If the 'Not Read' filter is active, keep only the rows the user has toggled locally.
   */
  private applyNotReadFilter(data: any[]): any[] {
    if (!this.notReadFilterActive) { return data; }
    return (data || []).filter(item => this.customerservice.isNotRead(item));
  }

  toggleNotRead(item: any, event: Event): void {
    event?.stopPropagation();
    const target = event?.target as HTMLInputElement | null;
    const checked = !!target?.checked;
    this.customerservice.setNotRead(item, checked);
    if (this.notReadFilterActive && !checked) {
      // Row no longer matches the active 'Not Read' filter -> drop it from the view
      this.tableData = this.tableData.filter(i => this.customerservice.getNotificationKey(i) !== this.customerservice.getNotificationKey(item));
    }
    this.changeDetect.detectChanges();
  }

  isToggleableRow(item: any): boolean {
    const original = item?.CustomerStatus || item?.Status;
    return original === 'Read';
  }

  getDisplayStatus(item: any): string {
    return this.customerservice.getDisplayStatus(item);
  }

  getTableFilterData(type, status){
    this.customerservice.getNotificationTableData(type, status).subscribe(
      (data) => {
        const filtered = this.applyNotReadFilter(data);
        this.tableData = this.applySorting(filtered);
        this.ogTableData = [...filtered];
        this.searchInput(this.searchValueSel || '');
        if(this.tableData.length < 1) {
          this.filterBool = true;
        }else{
          this.filterBool = false;
          this.searchBool = false;
        }
        this.changeDetect.detectChanges();
      },
      err => {
        this.initPageLoad = true;
      }
    );
  }

  getAccFilterData(accSelected, type, status){
    this.customerservice.getAccountDetails(accSelected, type, status).subscribe(
      data => {
        const filtered = this.applyNotReadFilter(data);
        this.tableData = this.applySorting(filtered);
        this.ogTableData = [...filtered];
        this.searchInput(this.searchValueSel || '');
        if(this.tableData.length < 1) {
          this.filterBool = true;
        }else{
          this.filterBool = false;
          this.searchBool = false;
        }
        this.changeDetect.detectChanges();
      },
      err => {
        const feedbackFailed = this.appUtils.getTranslatedMessage('AccountFailed');
        this.initPageLoad = true;
        snackbarService.show({
          message: this.translocoService.translate(feedbackFailed), 
          snackbarProps: {
            status: 'alarm',
            direction: 'horizontal',
            showClose: true,
            elevated: false,
            duration: 5000,
            verticalPlacement: 'top',
            horizontalPlacement: 'right',
          }
        });
      }
    );
  }

  searchInput(searchValueSel: any): void {
    const searchString = searchValueSel.toLowerCase().trim();
    const filteredData = this.ogTableData.filter(iter => {
      return this.appUtils.getLowerCase(iter.Type).trim().includes(searchString) 
      || this.appUtils.getLowerCase(iter.LocalId).trim().includes(searchString) 
      || this.appUtils.getLowerCase(iter.Title).trim().includes(searchString) 
      || this.appUtils.getLowerCase(iter.CustomerName || iter.AccountName).trim().includes(searchString)
      || this.appUtils.formatDateOnly(iter.DateDelivered).toLowerCase().trim().includes(searchString) 
      || this.appUtils.formatDateOnly(iter.FeedbackDueDate).toLowerCase().trim().includes(searchString)
      || this.appUtils.getLowerCase(this.customerservice.getDisplayStatus(iter)).trim().includes(searchString)
    });
    this.tableData = this.applySorting(filteredData);
    if(this.tableData.length < 1) {
      this.searchBool = true;
    }else{
      this.searchBool = false;
      this.filterBool = false;
    }
    this.changeDetect.detectChanges();
  }

  OpenNotificationDetails(row: any): void {
    const urlparam = `notificationId=${row.NotificationId}&contactnotificationid=${row.ContactNotificationId}&customerId=${row.CustomerId || row.AccountId}`;
    console.log('Opening notification details with params:', urlparam);
    this.router.navigate(['customer-notification-details'], { 
      queryParams: { 
        notificationId: row.NotificationId,
        contactNotificationId: row.ContactNotificationId,
        customerId: row.CustomerId || row.AccountId
      } });
  }

  public OnSort(column: string, value: string): void {
    const existingSortIndex = this.sortInfo.findIndex(sort => sort.column === column);
    if (existingSortIndex !== -1) {
      this.sortInfo.splice(existingSortIndex, 1);
    }
    if (value !== 'default') {
      this.sortInfo.push({ column, direction: value });
    }
    this.tableData = this.applySorting(this.ogTableData);
    this.changeDetect.detectChanges();
  }

  applySorting(data: any[]): any[] {
    const dataCopy = [...data]; 
    if (this.sortInfo.length > 0) {
      return dataCopy.sort((a, b) => {
        for (let i = this.sortInfo.length - 1; i >= 0; i--) {
          const { column, direction } = this.sortInfo[i];
          const aValue = a[column];
          const bValue = b[column];
          if (aValue < bValue) {
            return direction === 'asc' ? -1 : 1;
          } else if (aValue > bValue) {
            return direction === 'asc' ? 1 : -1;
          }
        }
        return 0;
      });
    } else {
      return dataCopy; 
    }
  }
}
